import { useState, useEffect } from 'react';
import { MailSidebar } from './MailSidebar';
import { MailList } from './MailList';
import { MailDetail } from './MailDetail';
import { ComposeModal } from './ComposeModal';
import { useSendGmailMessage } from '../hooks/useGmailChat';

export function EmailChatArea({
  companyId,
  conversations = [],
  isLoadingConversations = false,
  activeConversation = null,
  onSelectConversation,
  messages = [],
  isLoadingMessages = false,
  onSendMessage,
  searchQuery = '',
  onSearchChange,
  onRefresh,
  onNextPage,
  hasNextPage,
  isFetchingNextPage,
  totalApiCount,
  syncInfo = null,
  onStartSync,
  activeCategory,
  onCategoryChange,
  categories = [],
  activeFolder,
  onFolderChange,
  activeLabel,
  onLabelChange,
}) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [pendingNextIndex, setPendingNextIndex] = useState(null);

  useEffect(() => {
    if (pendingNextIndex !== null && conversations[pendingNextIndex]) {
      onSelectConversation?.(conversations[pendingNextIndex]);
      setPendingNextIndex(null);
    }
  }, [conversations, pendingNextIndex, onSelectConversation]);

  const sendGmailMessage = useSendGmailMessage();
  const currentCompanyId = companyId || localStorage.getItem('company_id');

  const handleSendNewEmail = async ({ to, cc, bcc, subject, body }) => {
    if (!currentCompanyId) return;

    // Optimistic close of modal
    setIsComposeOpen(false);

    try {
      await sendGmailMessage.mutateAsync({
        companyId: currentCompanyId,
        payload: {
          to,
          cc,
          bcc,
          subject,
          body,
        },
      });
      // Optionally trigger a refresh
      onRefresh?.();
    } catch (err) {
      console.error('Failed to send email:', err);
      // Re-open if failed, or handle toast
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-white rounded-[24px] overflow-hidden border border-gray-200/80 shadow-sm font-sans select-none relative">
      <ComposeModal
        isOpen={isComposeOpen}
        onClose={() => setIsComposeOpen(false)}
        onSend={handleSendNewEmail}
      />

      {/* Mobile Sidebar Overlay Toggle */}
      <div className="lg:hidden px-4 py-2.5 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
        <button
          type="button"
          onClick={() => setIsMobileSidebarOpen((p) => !p)}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-primary bg-surface-mint hover:bg-[#D5EBD2] border border-[#CDE5CA] rounded-xl shadow-xs transition-colors cursor-pointer"
        >
          {isMobileSidebarOpen ? (
            <>
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span>Close Folders</span>
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <span>Folders</span>
            </>
          )}
        </button>

        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100/90 text-gray-700 text-xs font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          <span className="capitalize">
            {activeLabel ? activeLabel : activeFolder || 'Inbox'}
          </span>
        </div>
      </div>

      <div className="flex flex-row flex-1 min-h-0 w-full relative overflow-hidden">
        {/* Mobile Sidebar Backdrop */}
        <div
          onClick={() => setIsMobileSidebarOpen(false)}
          className={`lg:hidden absolute inset-0 bg-black/30 backdrop-blur-[2px] z-30 transition-opacity duration-300 ease-in-out ${
            isMobileSidebarOpen
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={!isMobileSidebarOpen}
        />

        {/* Mobile Left Mail Sidebar Drawer (Smooth Slide-In) */}
        <div
          className={`lg:hidden absolute inset-y-0 left-0 w-64 max-w-[80vw] h-full bg-white z-40 shadow-2xl border-r border-gray-200 transition-transform duration-300 ease-out flex flex-col ${
            isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <MailSidebar
            activeFolder={activeFolder}
            onSelectFolder={(folderId) => {
              onFolderChange?.(folderId);
              setIsMobileSidebarOpen(false);
            }}
            activeLabel={activeLabel}
            onSelectLabel={(labelId) => {
              onLabelChange?.(labelId);
              setIsMobileSidebarOpen(false);
            }}
            onComposeClick={() => {
              onSelectConversation?.(null);
              setIsMobileSidebarOpen(false);
              setIsComposeOpen(true);
            }}
            labelsData={categories}
          />
        </div>

        {/* Desktop Left Mail Sidebar */}
        <div className="hidden lg:block w-56 shrink-0 h-full border-r border-gray-100 bg-white">
          <MailSidebar
            activeFolder={activeFolder}
            onSelectFolder={onFolderChange}
            activeLabel={activeLabel}
            onSelectLabel={onLabelChange}
            onComposeClick={() => {
              onSelectConversation?.(null);
              setIsComposeOpen(true);
            }}
            labelsData={categories}
          />
        </div>

        {/* Right Mail Main Panel (List View vs Detail View) */}
        <div className="flex-1 min-w-0 h-full bg-white flex flex-col overflow-hidden">
          {activeConversation ? (
            (() => {
              const currentIndex = conversations.findIndex(
                (c) =>
                  String(c.id || c.thread_id) ===
                  String(
                    activeConversation?.id || activeConversation?.thread_id,
                  ),
              );
              const hasPrevEmail = currentIndex > 0;
              const hasNextEmail =
                (currentIndex >= 0 &&
                  currentIndex < conversations.length - 1) ||
                Boolean(hasNextPage);

              return (
                <MailDetail
                  activeConversation={activeConversation}
                  messages={messages}
                  isLoadingMessages={isLoadingMessages}
                  onBack={() => onSelectConversation?.(null)}
                  onSendMessage={onSendMessage}
                  onPrevEmail={() => {
                    if (hasPrevEmail) {
                      onSelectConversation?.(conversations[currentIndex - 1]);
                    }
                  }}
                  onNextEmail={() => {
                    if (currentIndex < conversations.length - 1) {
                      onSelectConversation?.(conversations[currentIndex + 1]);
                    } else if (hasNextPage) {
                      setPendingNextIndex(currentIndex + 1);
                      onNextPage?.();
                    }
                  }}
                  hasPrevEmail={hasPrevEmail}
                  hasNextEmail={hasNextEmail}
                  currentIndex={currentIndex}
                  totalCount={totalApiCount || conversations.length}
                  isLoadingNext={
                    isFetchingNextPage || pendingNextIndex !== null
                  }
                  onDelete={() => {
                    onSelectConversation?.(null);
                  }}
                  onMarkUnread={() => {
                    onSelectConversation?.(null);
                  }}
                  onArchive={() => {
                    onSelectConversation?.(null);
                  }}
                />
              );
            })()
          ) : (
            <MailList
              conversations={conversations}
              isLoading={isLoadingConversations}
              activeConversationId={activeConversation?.id}
              onSelectConversation={onSelectConversation}
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              onRefresh={onRefresh}
              onNextPage={onNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              totalApiCount={totalApiCount}
              syncInfo={syncInfo}
              onStartSync={onStartSync}
              activeCategory={activeCategory}
              onCategoryChange={onCategoryChange}
              categories={categories}
              activeFolder={activeFolder}
              activeLabel={activeLabel}
            />
          )}
        </div>
      </div>
    </div>
  );
}
